import { DateTimePicker } from '@components/DateTimePicker'
import Modal from '@components/Modal'
import ConfirmationModal from '@components/ConfirmationModal'
import { useAuth } from '@context/AuthContext'
import { useDeleteFlight, usePatchFlight } from '@hooks'
import { FlightResponse, GetUserType } from '@orbit/api-types'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
	Button,
	ButtonGroup,
	ErrorText,
	FieldRow,
	FormDescription,
	FormGroup,
	FormInput,
	FormLabel,
	PassengerCheckbox,
	PassengerName,
	PassengerRowStyled,
	PassengersList,
	ProfileAvatar,
	SummaryBox,
} from '../AddFlightModal/AddFlightModal.styles'
import { datetimeLocalToWallClock, wallClockToDatetimeLocal } from '../flightHelpers'

interface EditLegModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	leg: FlightResponse
	members: GetUserType[]
	canDelete: boolean
}

const getInitials = (member: GetUserType) => {
	const first = member.firstName?.[0] ?? ''
	const last = member.lastName?.[0] ?? ''
	return (first + last).toUpperCase() || '?'
}

const EditLegModal: React.FC<EditLegModalProps> = ({ isOpen, onClose, tripId, leg, members, canDelete }) => {
	const { user } = useAuth()
	const patchFlight = usePatchFlight()
	const deleteFlight = useDeleteFlight()

	const [airlineIata, setAirlineIata] = useState('')
	const [flightNumber, setFlightNumber] = useState('')
	const [departureAirport, setDepartureAirport] = useState('')
	const [arrivalAirport, setArrivalAirport] = useState('')
	const [departureTime, setDepartureTime] = useState('')
	const [arrivalTime, setArrivalTime] = useState('')
	const [selectedPassengers, setSelectedPassengers] = useState<Set<number>>(new Set())
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

	useEffect(() => {
		if (isOpen && leg) {
			setAirlineIata(leg.airlineIata ?? '')
			setFlightNumber(leg.flightNumber ?? '')
			setDepartureAirport(leg.departureAirportCode ?? leg.departureAirport?.iataCode ?? '')
			setArrivalAirport(leg.arrivalAirportCode ?? leg.arrivalAirport?.iataCode ?? '')
			setDepartureTime(wallClockToDatetimeLocal(leg.departureTimeStr))
			setArrivalTime(wallClockToDatetimeLocal(leg.arrivalTimeStr))
			setSelectedPassengers(new Set(leg.passengers.map(p => p.userId)))
		}
	}, [isOpen, leg])

	const sortedMembers = useMemo(() => {
		return [...members].sort((a, b) => {
			if (a.id === user?.id) return -1
			if (b.id === user?.id) return 1
			return 0
		})
	}, [members, user?.id])

	const togglePassenger = (userId: number) => {
		setSelectedPassengers(prev => {
			const next = new Set(prev)
			if (next.has(userId)) {
				next.delete(userId)
			} else {
				next.add(userId)
			}
			return next
		})
	}

	const getMemberName = (member: GetUserType) => {
		const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ')
		return member.id === user?.id ? `${fullName} (You)` : fullName
	}

	const isManual = leg.isManuallyAdded

	const canSubmit = (() => {
		if (selectedPassengers.size === 0) return false
		if (patchFlight.isPending) return false
		if (!isManual) return true
		return airlineIata.trim().length > 0 && departureTime.length > 0
	})()

	const handleSubmit = async () => {
		if (!canSubmit) return
		const data = isManual
			? {
					airlineIata: airlineIata.trim().toUpperCase(),
					flightNumber: flightNumber.trim() || null,
					departureAirportCode: departureAirport.trim().toUpperCase() || null,
					arrivalAirportCode: arrivalAirport.trim().toUpperCase() || null,
					departureTimeStr: datetimeLocalToWallClock(departureTime),
					arrivalTimeStr: arrivalTime ? datetimeLocalToWallClock(arrivalTime) : null,
					passengerUserIds: Array.from(selectedPassengers),
				}
			: {
					passengerUserIds: Array.from(selectedPassengers),
				}

		try {
			await patchFlight.mutateAsync({ tripId, flightId: leg.id, data })
			toast.success('Flight leg updated!')
			onClose()
		} catch {
			toast.error('Unable to update flight leg. Please try again.')
		}
	}

	const handleDelete = async () => {
		if (!canDelete) {
			toast.error('This is the only leg in the flight. Delete the flight instead.')
			return
		}
		try {
			await deleteFlight.mutateAsync({ tripId, flightId: leg.id })
			toast.success('Flight leg deleted')
			setIsDeleteConfirmOpen(false)
			onClose()
		} catch {
			toast.error('Unable to delete flight leg. Please try again.')
		}
	}

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={isManual ? 'Edit Flight Leg' : 'Edit Passengers'}
				size="large"
				backgroundColor="#fff8f6"
				closeButtonColor="#a03c04"
				closeOnBackdropClick={false}
				onDelete={canDelete ? () => setIsDeleteConfirmOpen(true) : undefined}
				footer={
					<ButtonGroup>
						<Button type="button" variant="secondary" onClick={onClose}>
							Cancel
						</Button>
						<Button type="button" variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
							{patchFlight.isPending ? 'Saving…' : 'Save'}
						</Button>
					</ButtonGroup>
				}
			>
				{isManual ? (
					<>
						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-airline">Airline IATA</FormLabel>
								<FormInput
									id="edit-airline"
									type="text"
									value={airlineIata}
									onChange={e => setAirlineIata(e.target.value.toUpperCase())}
									maxLength={3}
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-number">Flight Number</FormLabel>
								<FormInput
									id="edit-number"
									type="text"
									value={flightNumber}
									onChange={e => setFlightNumber(e.target.value)}
									maxLength={6}
								/>
							</FormGroup>
						</FieldRow>

						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-dep-airport">Departure Airport</FormLabel>
								<FormInput
									id="edit-dep-airport"
									type="text"
									value={departureAirport}
									onChange={e => setDepartureAirport(e.target.value.toUpperCase())}
									maxLength={3}
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-arr-airport">Arrival Airport</FormLabel>
								<FormInput
									id="edit-arr-airport"
									type="text"
									value={arrivalAirport}
									onChange={e => setArrivalAirport(e.target.value.toUpperCase())}
									maxLength={3}
								/>
							</FormGroup>
						</FieldRow>

						<FieldRow>
							<FormGroup>
								<FormLabel htmlFor="edit-dep-time">Departure Time</FormLabel>
								<DateTimePicker
									id="edit-dep-time"
									value={departureTime}
									onChange={next => {
										setDepartureTime(next)
										if (arrivalTime && next && next > arrivalTime) {
											setArrivalTime('')
										}
									}}
									placeholder="Select departure"
								/>
							</FormGroup>
							<FormGroup>
								<FormLabel htmlFor="edit-arr-time">Arrival Time (Optional)</FormLabel>
								<DateTimePicker
									id="edit-arr-time"
									value={arrivalTime}
									onChange={next => {
										setArrivalTime(next)
										if (departureTime && next && next < departureTime) {
											setDepartureTime('')
										}
									}}
									placeholder="Select arrival"
								/>
							</FormGroup>
						</FieldRow>
					</>
				) : (
					<FormGroup>
						<FormDescription>
							Auto-imported flights can only edit passengers. To change flight details, delete this leg and add it manually.
						</FormDescription>
					</FormGroup>
				)}

				<FormGroup>
					<FormLabel>Passengers</FormLabel>
					<FormDescription>Select who is on this leg</FormDescription>
					<PassengersList>
						{sortedMembers.map(member => {
							const isSelected = selectedPassengers.has(member.id)
							return (
								<PassengerRowStyled
									key={member.id}
									$isSelected={isSelected}
									onClick={() => togglePassenger(member.id)}
								>
									<PassengerCheckbox $checked={isSelected} />
									<ProfileAvatar>
										{member.profilePicture ? (
											<img src={member.profilePicture} alt={getMemberName(member)} />
										) : (
											getInitials(member)
										)}
									</ProfileAvatar>
									<PassengerName>{getMemberName(member)}</PassengerName>
								</PassengerRowStyled>
							)
						})}
					</PassengersList>
					<SummaryBox>
						{selectedPassengers.size === 0 ? (
							<ErrorText>Select at least one passenger</ErrorText>
						) : (
							`${selectedPassengers.size} ${selectedPassengers.size === 1 ? 'passenger' : 'passengers'} selected`
						)}
					</SummaryBox>
				</FormGroup>
			</Modal>

			<ConfirmationModal
				isOpen={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				onConfirm={handleDelete}
				title="Delete Flight Leg"
				message="Are you sure you want to delete this flight leg? This cannot be undone."
				confirmText="Delete"
				cancelText="Cancel"
				variant="danger"
			/>
		</>
	)
}

export default EditLegModal
